import { useEffect, useRef, useState } from "react";
import { Check, Copy, Phone } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { canPlaceCall, toTelHref } from "@/lib/hcbb-cost-estimator-utils";

/**
 * Dials a row's number through the device's handler.
 *
 * Rendered as an anchor so a long-press or a copy still works on desktop, and
 * as a disabled button when the payload has no number — the one place that
 * decision is made, for both detail screens alike. The number *is* the
 * visible label, so the accessible name is composed around it rather than
 * replaced by `aria-label`, which would leave speech-input users saying a
 * phrase the screen does not show.
 */
export const HcbbPhoneButton = ({
  name,
  phone,
}: {
  /** Whose number this is; the accessible name has to say. */
  name: string;
  phone: string;
}) => {
  const telHref = toTelHref(phone);

  if (!telHref) {
    return (
      <Button variant="default" disabled>
        <Phone className="text-gray-800" aria-hidden="true" />
        No phone number on file
      </Button>
    );
  }

  return (
    <Button href={telHref} linkStyle="button" variant="default">
      <Phone aria-hidden="true" />
      <span className="sr-only">Call {name} at </span>
      {phone}
    </Button>
  );
};

/** How long the copied/failed state holds before the icon reverts. */
const COPY_FEEDBACK_MS = 2000;

type CopyState = "idle" | "copied" | "failed";

/**
 * A result card's phone control, sitting between the map and details buttons.
 *
 * Clicking dials on a device that can actually place the call (mobile OSes,
 * per `canPlaceCall`); everywhere else — desktop, the majority of this
 * portal's traffic, where a `tel:` link does nothing visible — the click
 * copies the number instead. Hovering (or focusing) always reveals the
 * number in a card alongside its own copy control, so the number is
 * available to copy regardless of what the button itself just did.
 *
 * `HoverCard`, not `Tooltip`: Radix's tooltip renders its content twice — once
 * visible, once again inside a visually-hidden node for screen readers — so a
 * `<button>` placed in `TooltipContent` is duplicated in the DOM. `HoverCard`
 * carries no such duplication and is built for exactly this — hover-revealed
 * content with its own interactive control.
 */
export const HcbbPhoneAction = ({
  name,
  phone,
}: {
  /** Whose number this is, for each control's accessible name. */
  name: string;
  phone: string;
}) => {
  const [copyState, setCopyState] = useState<CopyState>("idle");
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Only the last-mounted row's own timer should ever touch this row's
  // state — clearing on unmount is what keeps a slow copy from setting state
  // on a row the member has since scrolled a hundred more results past.
  useEffect(() => () => clearTimeout(resetTimeoutRef.current), []);

  const telHref = toTelHref(phone);

  if (!telHref) {
    return (
      <Button
        variant="outline"
        disabled
        aria-label={`No phone number on file for ${name}`}
      >
        <Phone aria-hidden="true" />
      </Button>
    );
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(phone);
      setCopyState("copied");
    } catch {
      // Clipboard access can fail — an insecure origin, a denied permission —
      // and the number stays right there either way, selectable by hand, so
      // this is worth telling the member rather than doing nothing silently.
      setCopyState("failed");
    }

    clearTimeout(resetTimeoutRef.current);
    resetTimeoutRef.current = setTimeout(
      () => setCopyState("idle"),
      COPY_FEEDBACK_MS,
    );
  };

  const canCall = canPlaceCall();

  return (
    <HoverCard openDelay={150} closeDelay={150}>
      <HoverCardTrigger asChild>
        {canCall ? (
          <Button
            href={telHref}
            linkStyle="button"
            variant="outline"
            aria-label={`Call ${name} at ${phone}`}
          >
            <Phone aria-hidden="true" />
          </Button>
        ) : (
          <Button
            type="button"
            variant="outline"
            onClick={() => void handleCopy()}
            aria-label={`Copy ${name}'s phone number, ${phone}`}
          >
            <Phone aria-hidden="true" />
          </Button>
        )}
      </HoverCardTrigger>
      <HoverCardContent className="w-auto px-3 py-2">
        <button
          type="button"
          onClick={() => void handleCopy()}
          aria-label={`Copy ${name}'s phone number, ${phone}`}
          className="inline-flex items-center gap-1 text-sm font-medium text-gray-800 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {phone}
          {copyState === "copied" ? (
            <Check aria-hidden="true" height={14} className="text-green-700" />
          ) : (
            <Copy aria-hidden="true" height={14} />
          )}
        </button>
      </HoverCardContent>
      {/* Present before the click, not mounted by it — a live region that
          only enters the DOM already holding its message announces nothing. */}
      <span role="status" className="sr-only">
        {copyState === "copied" && `${name}'s phone number copied.`}
        {copyState === "failed" &&
          "Could not copy the phone number. Select it to copy by hand."}
      </span>
    </HoverCard>
  );
};
